import React, { useMemo, useRef, Suspense } from "react";
import { Canvas, useThree, useFrame, extend } from "@react-three/fiber";
import { OrbitControls as ThreeOrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

extend({ ThreeOrbitControls });

function Controls() {
  const { camera, gl } = useThree();
  const ref = useRef();
  useFrame(() => ref.current?.update());
  return <threeOrbitControls ref={ref} args={[camera, gl.domElement]} enableDamping dampingFactor={0.08} />;
}

const WALL_H = 3.2;
const DOOR_H = 2.1;
const SILL = 0.9;
const WIN_TOP = 2.2;

// Split one wall (length L) into full-height / sill / header segments around
// its openings, leaving real voids for doors and glazed gaps for windows.
function wallSegments(L, ops, H) {
  const segs = [];
  const sorted = [...ops].sort((a, b) => a.c - b.c);
  let cursor = 0;
  for (const o of sorted) {
    const a = Math.max(0, o.c - o.w / 2);
    const b = Math.min(L, o.c + o.w / 2);
    if (a <= cursor) { cursor = Math.max(cursor, b); continue; }
    if (a > cursor) segs.push({ x0: cursor, x1: a, y0: 0, y1: H });
    if (o.kind === "door") {
      if (H > DOOR_H) segs.push({ x0: a, x1: b, y0: DOOR_H, y1: H });
    } else {
      segs.push({ x0: a, x1: b, y0: 0, y1: Math.min(SILL, H) });
      if (H > WIN_TOP) segs.push({ x0: a, x1: b, y0: WIN_TOP, y1: H });
      segs.push({ x0: a, x1: b, y0: Math.min(SILL, H), y1: Math.min(WIN_TOP, H), glass: true });
    }
    cursor = b;
  }
  if (cursor < L) segs.push({ x0: cursor, x1: L, y0: 0, y1: H });
  return segs;
}

function Building({ model, floors, wallHeight }) {
  const data = useMemo(() => {
    const walls = (model.walls || []).filter((w) => w?.p1 && w?.p2);
    if (!walls.length) return null;
    let xmin = Infinity, xmax = -Infinity, zmin = Infinity, zmax = -Infinity;
    walls.forEach((w) => {
      xmin = Math.min(xmin, w.p1.x, w.p2.x); xmax = Math.max(xmax, w.p1.x, w.p2.x);
      zmin = Math.min(zmin, w.p1.y, w.p2.y); zmax = Math.max(zmax, w.p1.y, w.p2.y);
    });
    const cx = (xmin + xmax) / 2, cz = (zmin + zmax) / 2;
    const fw = xmax - xmin, fd = zmax - zmin;
    const boxes = [];
    for (let f = 0; f < floors; f++) {
      const baseY = f * wallHeight;
      walls.forEach((w) => {
        const dx = w.p2.x - w.p1.x, dz = w.p2.y - w.p1.y;
        const L = Math.hypot(dx, dz) || 0.01;
        const ux = dx / L, uz = dz / L;
        const rotY = -Math.atan2(dz, dx);
        const t = Number(w.thickness) || ((w.wall_type || "").toLowerCase() === "exterior" ? 0.25 : 0.15);
        const ops = (model.openings || [])
          .filter((o) => o.wall_id === w.id)
          .map((o) => ({ c: Number(o.offset_from_p1) || 0, w: Number(o.width) || 0.9, kind: o.kind }));
        for (const s of wallSegments(L, ops, wallHeight)) {
          const segLen = s.x1 - s.x0, segH = s.y1 - s.y0;
          if (segLen <= 0.01 || segH <= 0.01) continue;
          const mid = (s.x0 + s.x1) / 2;
          boxes.push({
            key: `${f}-${w.id}-${s.x0.toFixed(2)}-${s.y0}`,
            pos: [w.p1.x + ux * mid - cx, baseY + (s.y0 + s.y1) / 2, w.p1.y + uz * mid - cz],
            size: [segLen, segH, s.glass ? t * 0.4 : t],
            rotY,
            glass: !!s.glass,
          });
        }
      });
    }
    const columns = (model.columns || []).filter((c) => c?.centre);
    const F2 = Number.isFinite;
    const zones = (model.room_labels_pos || []).filter(
      (z) => z && F2(z.x) && F2(z.y) && F2(z.w) && F2(z.h) && z.zone_kind
    );
    const stairs = (model.stairs || []).filter(
      (s) => s && s.origin && F2(s.origin.x) && F2(s.origin.y) && F2(s.width) && F2(s.depth)
    );
    return { cx, cz, fw, fd, xmin, zmin, boxes, columns, zones, stairs, topY: floors * wallHeight };
  }, [model, floors, wallHeight]);

  if (!data) return null;
  const { cx, cz, fw, fd, boxes, columns, zones, stairs, topY } = data;

  const ZONE_COLOR = (k) => {
    const s = (k || "").toLowerCase();
    if (s.includes("pool")) return "#0EA5E9";
    if (s.includes("garden")) return "#10B981";
    if (s.includes("courtyard")) return "#F59E0B";
    return "#A78BFA"; // terrace / balcony
  };

  return (
    <group>
      {/* floor slabs (one per level + roof) */}
      {Array.from({ length: floors + 1 }).map((_, i) => (
        <mesh key={`slab${i}`} position={[0, i * wallHeight - 0.1, 0]} receiveShadow>
          <boxGeometry args={[fw + 0.4, 0.2, fd + 0.4]} />
          <meshStandardMaterial color={i === 0 ? "#94A3B8" : "#CBD5E1"} />
        </mesh>
      ))}
      {/* walls */}
      {boxes.map((b) => (
        <mesh key={b.key} position={b.pos} rotation={[0, b.rotY, 0]} castShadow receiveShadow>
          <boxGeometry args={b.size} />
          {b.glass
            ? <meshStandardMaterial color="#38BDF8" transparent opacity={0.4} metalness={0.3} roughness={0.1} />
            : <meshStandardMaterial color="#E8ECF2" />}
        </mesh>
      ))}
      {/* columns */}
      {columns.map((c, i) => (
        <mesh key={`col${i}`} position={[c.centre.x - cx, topY / 2 - 0.1, c.centre.y - cz]} rotation={[0, -((c.angle || 0) * Math.PI) / 180, 0]} castShadow>
          <boxGeometry args={[c.width || 0.4, topY, c.depth || 0.4]} />
          <meshStandardMaterial color="#334155" />
        </mesh>
      ))}
      {/* terraces / balconies / pools / gardens / courtyards - outdoor zones */}
      {zones.map((z, i) => (
        <group key={`zone${i}`} position={[z.x - cx, 0, z.y - cz]}>
          <mesh position={[0, 0.06, 0]} receiveShadow>
            <boxGeometry args={[z.w, 0.12, z.h]} />
            <meshStandardMaterial color={ZONE_COLOR(z.zone_kind)} transparent opacity={0.7} />
          </mesh>
          {/* low railing on the outer (far) edge for terrace/balcony */}
          {/terrace|balcony/i.test(z.zone_kind || "") && (
            <mesh position={[0, 0.5, z.h / 2]} castShadow>
              <boxGeometry args={[z.w, 0.9, 0.06]} />
              <meshStandardMaterial color={ZONE_COLOR(z.zone_kind)} transparent opacity={0.5} />
            </mesh>
          )}
        </group>
      ))}
      {/* stairs - stepped flight */}
      {stairs.map((s, si) => {
        const n = Math.max(2, Math.min(Number(s.steps) || 12, 20));
        const sw = Number(s.width), sd = Number(s.depth);
        const vertical = (s.orientation || "vertical") === "vertical";
        const riseTotal = Math.min(wallHeight, 3.2);
        const ox = s.origin.x - cx, oz = s.origin.y - cz;
        const steps = [];
        for (let i = 0; i < n; i++) {
          const hStep = (riseTotal * (i + 1)) / n;
          if (vertical) {
            const dz = sd / n;
            steps.push(
              <mesh key={`s${si}-${i}`} position={[ox + sw / 2, hStep / 2, oz + (i + 0.5) * dz]} castShadow receiveShadow>
                <boxGeometry args={[sw, hStep, dz]} />
                <meshStandardMaterial color="#64748B" />
              </mesh>
            );
          } else {
            const dx = sw / n;
            steps.push(
              <mesh key={`s${si}-${i}`} position={[ox + (i + 0.5) * dx, hStep / 2, oz + sd / 2]} castShadow receiveShadow>
                <boxGeometry args={[dx, hStep, sd]} />
                <meshStandardMaterial color="#64748B" />
              </mesh>
            );
          }
        }
        return <group key={`stair${si}`}>{steps}</group>;
      })}
    </group>
  );
}

/**
 * Plan3DViewer - real orbitable 3D massing extruded from the approved 2D
 * model: walls with height & thickness, actual door/window voids, floor
 * slabs, stacked floors and columns. Rendered from the same geometry that
 * drives the 2D plan and IFC export, so it is accurate - not decorative.
 */
export default function Plan3DViewer({ model, floors = 1, wallHeight = WALL_H, className = "h-[560px]" }) {
  const span = Math.max(20, (floors + 1) * wallHeight * 2);
  return (
    <div className={`overflow-hidden rounded-xl border border-border bg-gradient-to-b from-slate-800 to-slate-950 ${className}`}>
      <Canvas shadows camera={{ position: [span * 0.7, span * 0.6, span * 0.7], fov: 45 }}>
        <Suspense fallback={null}>
          <hemisphereLight args={["#ffffff", "#334155", 0.9]} />
          <ambientLight intensity={0.35} />
          <directionalLight position={[30, 50, 20]} intensity={1.1} castShadow shadow-mapSize={[1024, 1024]} />
          <Building model={model} floors={floors} wallHeight={wallHeight} />
          <gridHelper args={[120, 60, "#475569", "#1E293B"]} position={[0, -0.2, 0]} />
          <Controls />
        </Suspense>
      </Canvas>
    </div>
  );
}
